/**
 * mediaDownload · iter790
 * ─────────────────────────────────────────────────────────────
 * iter672 · Native iOS save-to-Photos via @capacitor-community/media.
 * The old @capacitor/share path required the user to manually tap
 * "Save Image" and, when it threw, fell through to window.open() —
 * which just dumped the raw res.cloudinary.com URL into an in-app
 * browser and saved NOTHING (the "random Cloudinary graphic" Jay
 * saw). Now:
 *   1. Native → Media.savePhoto({ path: <url> }) writes straight to
 *      the camera roll. First save triggers the iOS "Add to Photos"
 *      permission prompt. No browser, no manual tap.
 *   2. If savePhoto throws → fall back to the iOS Share sheet with the
 *      file (never window.open on native).
 *   3. Web (dev preview / Android web) → classic <a download>.
 * Returns the count actually saved so the caller can show an honest
 * "Saved N to Photos" confirmation.
 * iter768 · `items` can be plain URL strings (legacy photo-only
 * callers) or `{ url, isVideo }` objects — video needs the FULL-quality
 * download URL (not the compressed in-app preview) and a different
 * native save call (Media.saveVideo vs savePhoto).
 * iter790 · Extracted out of AthleteGameFolderPage so PhotoLightbox's
 * video "Full quality" button can reuse the same native-save path
 * instead of a plain <a href> — which on iOS just opened the raw
 * Cloudinary URL in a browser instead of saving anything.
 */
import { Capacitor } from '@capacitor/core';
export const downloadAssets = async (items) => {
  if (!items.length) return 0;
  const entries = items.map((it) => (typeof it === 'string' ? { url: it, isVideo: false } : it));
  const isNative = Capacitor?.isNativePlatform?.();
  if (isNative) {
    // Fetch → base64 → cache file, so savePhoto/Share both have a
    // local URI even if the remote host is picky about direct saves.
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const stamp = Date.now();
    const toCacheFile = async (url, i) => {
      const originalUrl = url.replace(/\/image\/upload\/[^/]*\//, '/image/upload/');
      const res = await fetch(originalUrl, { mode: 'cors' });
      if (!res.ok) throw new Error(`fetch ${res.status}`);
      const blob = await res.blob();
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result).split(',')[1] || '');
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
      const written = await Filesystem.writeFile({
        path: `flyt-${stamp}-${i + 1}.${ext}`,
        data: base64,
        directory: Directory.Cache,
      });
      return written?.uri;
    };
    // ── Primary: save each photo/video straight to the camera roll. ──
    try {
      const { Media } = await import('@capacitor-community/media');
      let saved = 0;
      for (let i = 0; i < entries.length; i++) {
        const uri = await toCacheFile(entries[i].url, i);
        if (!uri) continue;
        // savePhoto/saveVideo accept a web URL, base64 URI, or local file path.
        if (entries[i].isVideo && typeof Media.saveVideo === 'function') {
          await Media.saveVideo({ path: uri });
        } else {
          await Media.savePhoto({ path: uri });
        }
        saved += 1;
      }
      if (saved > 0) return saved;
      throw new Error('nothing saved');
    } catch (err) {
      console.warn('[flyt] Media.savePhoto failed, falling back to share sheet', err);
    }
    // ── Fallback: iOS Share sheet with the files (NOT window.open). ──
    try {
      const { Share } = await import('@capacitor/share');
      const fileUris = [];
      for (let i = 0; i < entries.length; i++) {
        const uri = await toCacheFile(entries[i].url, i);
        if (uri) fileUris.push(uri);
      }
      if (fileUris.length === 0) throw new Error('no files written');
      await Share.share({
        title: fileUris.length === 1 ? 'Save file' : `Save ${fileUris.length} files`,
        files: fileUris,
        dialogTitle: 'Save to Photos',
      });
      return fileUris.length;
    } catch (err2) {
      console.warn('[flyt] share-sheet fallback failed', err2);
      throw err2; // let the caller surface a real error toast
    }
  }
  // Web path — <a download> per URL. Kept minimal so Safari doesn't
  // block the second file.
  for (const { url } of entries) {
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = url.split('/').pop().split('?')[0] || 'photo.jpg';
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      await new Promise((r) => setTimeout(r, 120));
    } catch {
      window.open(url, '_blank');
    }
  }
  return entries.length;
};
